import React from 'react';
import { Eye, ArrowUp, ArrowDown } from 'lucide-react';
import { formatFilterValue, type ListView } from '../../../services/queries/listViewQueries';

interface ListViewPreviewProps {
  view: Partial<ListView>;
  objectLabel: string;
}

export const ListViewPreview: React.FC<ListViewPreviewProps> = ({ view, objectLabel }) => {
  // Generate sample data based on columns
  const sampleData = generateSampleData(view.columns || [], 5);

  return (
    <div className="w-96 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2 mb-3">
          <Eye className="w-5 h-5 text-purple-600 dark:text-purple-400" />
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Live Preview</h3>
        </div>
        <p className="text-xs text-gray-600 dark:text-gray-400">
          Preview of how your {objectLabel} list will appear
        </p>
      </div>

      {/* Preview Content */}
      <div className="flex-1 overflow-auto p-4 bg-gray-50 dark:bg-gray-900">
        {view.columns && view.columns.length > 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            {/* Table Header */}
            <div className="bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
              <div className="flex">
                {view.columns.map((column, index) => {
                  const sortConfig = view.sort?.find(s => s.fieldId === column.fieldId);

                  return (
                    <div
                      key={column.fieldId}
                      className={`flex-1 px-3 py-2 text-xs font-semibold text-gray-700 dark:text-gray-300 ${
                        index !== 0 ? 'border-l border-gray-200 dark:border-gray-700' : ''
                      }`}
                    >
                      <div className="flex items-center gap-1">
                        <span className="truncate">{column.fieldLabel}</span>
                        {sortConfig && (
                          sortConfig.direction === 'asc' ? (
                            <ArrowUp className="w-3 h-3 text-purple-600 dark:text-purple-400 flex-shrink-0" />
                          ) : (
                            <ArrowDown className="w-3 h-3 text-purple-600 dark:text-purple-400 flex-shrink-0" />
                          )
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Table Body */}
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {sampleData.map((row, rowIndex) => (
                <div key={rowIndex} className="flex hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                  {view.columns!.map((column, colIndex) => (
                    <div
                      key={column.fieldId}
                      className={`flex-1 px-3 py-2 text-xs text-gray-900 dark:text-white ${
                        colIndex !== 0 ? 'border-l border-gray-200 dark:border-gray-700' : ''
                      }`}
                    >
                      <span className="truncate block">{row[column.fieldId]}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <Eye className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Add columns to see preview
            </p>
          </div>
        )}

        {/* Filter Summary */}
        {view.filters && view.filters.length > 0 && (
          <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <div className="text-xs font-semibold text-blue-900 dark:text-blue-300 mb-2">
              Active Filters ({view.filters.length})
            </div>
            <div className="space-y-1">
              {view.filters.map((filter, index) => (
                <div key={filter.id} className="text-xs text-blue-800 dark:text-blue-400">
                  {index > 0 && <span className="font-semibold">AND </span>}
                  <span className="font-medium">{filter.fieldLabel}</span>
                  {' '}
                  {filter.operator}
                  {!['isNull', 'isNotNull'].includes(filter.operator) && (
                    <>
                      {' '}
                      <span className="font-semibold">
                        {formatFilterValue(filter.value, filter.fieldType)}
                      </span>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Footer Stats */}
      <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
        <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
          <div className="flex justify-between">
            <span>Columns:</span>
            <span className="font-semibold text-gray-900 dark:text-white">
              {view.columns?.length || 0}
            </span>
          </div>
          <div className="flex justify-between">
            <span>Filters:</span>
            <span className="font-semibold text-gray-900 dark:text-white">
              {view.filters?.length || 0}
            </span>
          </div>
          <div className="flex justify-between">
            <span>Sort Fields:</span>
            <span className="font-semibold text-gray-900 dark:text-white">
              {view.sort?.length || 0}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================
// HELPER FUNCTION TO GENERATE SAMPLE DATA
// ============================================

function generateSampleData(columns: any[], rows: number): Record<string, string>[] {
  const sampleValues: Record<string, string[]> = {
    text: ['Acme Corp', 'Global Industries', 'Tech Solutions', 'Dynamic Systems', 'Innovation Labs'],
    email: ['contact@acme.com', 'info@global.com', 'hello@tech.com', 'support@dynamic.com', 'team@innovation.com'],
    phone: ['+1 555-0100', '+1 555-0200', '+1 555-0300', '+1 555-0400', '+1 555-0500'],
    currency: ['$150,000', '$275,000', '$500,000', '$125,000', '$350,000'],
    number: ['100', '250', '500', '75', '300'],
    date: ['2025-01-15', '2025-02-20', '2025-03-10', '2025-04-05', '2025-05-12'],
    picklist: ['Active', 'Pending', 'Inactive', 'Active', 'Pending'],
    lookup: ['John Smith', 'Jane Doe', 'Bob Johnson', 'Alice Williams', 'Charlie Brown']
  };

  return Array.from({ length: rows }, (_, rowIndex) => {
    const row: Record<string, string> = {};
    columns.forEach(column => {
      const values = sampleValues[column.fieldType] || sampleValues.text;
      row[column.fieldId] = values[rowIndex % values.length];
    });
    return row;
  });
}
